import { useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { gramsPer } from '@zilian/contracts';
import { api } from './api';
import { byWeight, partsFor } from './recipe';
import SkuCreateDialog, { type Category } from './SkuCreateDialog';
import { amount, decimalInput, QTY_DECIMALS, skuPicker } from './ui';

interface Sku {
  id: string;
  code: string;
  name: string;
  spec: string;
  unit: string;
  usage: string;
  integerUnit: boolean;
  unitWeight: string | null;
}

interface Props {
  /** A product to start from, e.g. when sent here from a stock adjustment. */
  productId?: string | null;
  skus: Sku[];
  recipes: { product_id: string; material_id: string; kind: string; ratio: string }[];
  categories: Category[];
  warehouses: { value: string; label: string }[];
  onClose: () => void;
  onDone: (message: string) => void;
  /** The product's recipe or weight was edited from here; the caller reloads them. */
  onChanged: () => void;
  /** Whether the member maintains item records; otherwise the recipe is shown, not editable. */
  canEdit: boolean;
}

const label = (s: Sku) => `${s.code} · ${s.name}${s.spec ? ` · ${s.spec}` : ''}`;
/** A computed use for people to read: weights to 4 places, whole parts rounded up. */
const show = (value: number, sku: Sku) =>
  sku.integerUnit
    ? Math.ceil(value - 1e-9).toLocaleString('zh-CN')
    : value.toLocaleString('zh-CN', { maximumFractionDigits: 4 });

/**
 * 生产入库: pick the product and count the good pieces. What the materials lose comes from the
 * product's recipe and piece weight in its item record, shown here to check, not to edit.
 */
export default function ProductionDialog(p: Props) {
  const products = p.skus.filter((s) => s.usage !== 'PURCHASE');
  const [productId, setProductId] = useState<string | null>(p.productId ?? null);
  const [quantity, setQuantity] = useState('');
  const [warehouseId, setWarehouseId] = useState(p.warehouses[0]?.value ?? '');
  const [balances, setBalances] = useState<any[] | null>(null);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [operationKey] = useState(() => crypto.randomUUID());
  const product = products.find((s) => s.id === productId);
  const skuOf = (id: string) => p.skus.find((s) => s.id === id);
  const lines = p.recipes
    .filter((r) => r.product_id === productId && r.kind === 'PRODUCE')
    .map((r) => ({ skuId: r.material_id, ratio: r.ratio }));
  const parts = partsFor(lines, skuOf);
  const weight = Number(product?.unitWeight) || 0;
  const pieces = Number(quantity) || 0;
  const totalGrams = pieces * weight;
  // Set up in the item record; until then nothing can be worked out.
  const missing = !product
    ? null
    : !lines.length
      ? '该产品还没有设置生产用料，无法计算原料扣减。'
      : parts.weighed.length && !weight
        ? '该产品还没有设置单重，无法计算按重量扣减的原料。'
        : null;

  useEffect(() => {
    api<any[]>('/inventory')
      .then(setBalances)
      .catch(() => setBalances([]));
  }, []);

  const useOf = (line: (typeof lines)[number]): number | null => {
    const sku = skuOf(line.skuId);
    if (!sku || !pieces || missing) return null;
    const grams = gramsPer(sku.unit);
    if (grams) return (totalGrams * parts.partsOf(line)) / parts.total / grams;
    return pieces * Number(line.ratio);
  };
  const availableOf = (skuId: string) =>
    balances
      ? Number(
          balances.find(
            (b) =>
              b.sku_id === skuId && b.warehouse_id === warehouseId && b.quality === 'AVAILABLE',
          )?.available ?? 0,
        )
      : null;
  const shortOf = (line: (typeof lines)[number]) => {
    const use = useOf(line);
    const available = availableOf(line.skuId);
    return use !== null && available !== null && use > available + 1e-9;
  };
  const short = lines.filter(shortOf);

  async function submit() {
    if (busy) return;
    if (!product) return setError('请选择生产的产品');
    if (!(pieces > 0)) return setError('请填写合格数量');
    setBusy(true);
    setError('');
    try {
      await api('/inventory/produce', {
        warehouseId,
        skuId: product.id,
        quantity: quantity.replace(/\.$/, ''),
        key: operationKey,
      });
      p.onDone(`已生产入库 ${product.name} ${amount(quantity)} ${product.unit}`);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Dialog open fullWidth maxWidth="md" onClose={() => !busy && p.onClose()}>
        <DialogTitle sx={{ pb: 1 }}>
          生产入库
          <Typography color="text.secondary" sx={{ fontSize: 13, mt: 0.5 }}>
            填写合格数量，原料按商品档案中的单重和生产用料自动扣减。
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}
            {!error && short.length > 0 && (
              <Alert severity="warning">
                原料不足：
                {short
                  .map((l) => {
                    const s = skuOf(l.skuId)!;
                    return `${s.name} 需 ${show(useOf(l)!, s)} ${s.unit}，可用 ${amount(availableOf(l.skuId))} ${s.unit}`;
                  })
                  .join('；')}
                。请先登记原料入库或期初。
              </Alert>
            )}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Autocomplete<any>
                sx={{ flex: 1 }}
                options={products}
                value={product ?? null}
                disabled={busy}
                {...skuPicker()}
                onChange={(_, v) => {
                  setProductId(v?.id ?? null);
                  setError('');
                }}
                renderInput={(params) => <TextField {...params} autoFocus label="产品" />}
              />
              <TextField
                label="合格数量"
                value={quantity}
                disabled={busy}
                sx={{ width: 170 }}
                slotProps={{
                  htmlInput: { inputMode: 'decimal' },
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">{product?.unit ?? '个'}</InputAdornment>
                    ),
                  },
                }}
                onChange={(e) =>
                  setQuantity(
                    product?.integerUnit
                      ? e.target.value.replace(/\D/g, '')
                      : decimalInput(e.target.value, QTY_DECIMALS),
                  )
                }
              />
              {p.warehouses.length > 1 && (
                <TextField
                  select
                  label="仓库"
                  value={warehouseId}
                  disabled={busy}
                  sx={{ width: 160 }}
                  onChange={(e) => setWarehouseId(e.target.value)}
                >
                  {p.warehouses.map((w) => (
                    <MenuItem key={w.value} value={w.value}>
                      {w.label}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            </Box>
            {product && missing && (
              <Alert
                severity="info"
                action={
                  p.canEdit && (
                    <Button color="inherit" size="small" onClick={() => setEditing(true)}>
                      去设置
                    </Button>
                  )
                }
              >
                {missing}
                {!p.canEdit && '请联系负责商品档案的同事设置。'}
              </Alert>
            )}
            {product && lines.length > 0 && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mb: 0.5 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 650 }}>原料扣减</Typography>
                  {weight > 0 && parts.weighed.length > 0 && (
                    <Typography color="text.secondary" sx={{ fontSize: 12.5 }}>
                      单重 {weight} g
                      {totalGrams > 0 &&
                        ` · 合计 ${(totalGrams / 1000).toLocaleString('zh-CN', { maximumFractionDigits: 3 })} kg`}
                    </Typography>
                  )}
                  {p.canEdit && (
                    <Button
                      size="small"
                      disabled={busy}
                      sx={{ ml: 'auto', minWidth: 0 }}
                      onClick={() => setEditing(true)}
                    >
                      修改用料
                    </Button>
                  )}
                </Box>
                <Table size="small" sx={{ '& th': { whiteSpace: 'nowrap' }, '& td': { py: 1.25 } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>原料</TableCell>
                      <TableCell sx={{ width: 150 }}>配比</TableCell>
                      <TableCell align="right" sx={{ width: 150 }}>
                        本次扣减
                      </TableCell>
                      <TableCell align="right" sx={{ width: 130 }}>
                        可用库存
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {lines.map((line) => {
                      const sku = skuOf(line.skuId);
                      if (!sku) return null;
                      const use = useOf(line);
                      const available = availableOf(line.skuId);
                      const percent = parts.percent(line);
                      return (
                        <TableRow key={line.skuId}>
                          <TableCell>{label(sku)}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary' }}>
                            {byWeight(sku)
                              ? parts.lone
                                ? '100%'
                                : `${Number(line.ratio)} 份 · ${percent}%`
                              : `每${product.unit} ${Number(line.ratio)} ${sku.unit}`}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              whiteSpace: 'nowrap',
                              fontWeight: 650,
                              color: shortOf(line) ? 'error.main' : 'text.primary',
                            }}
                          >
                            {use !== null ? `${show(use, sku)} ${sku.unit}` : '—'}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ whiteSpace: 'nowrap', color: 'text.secondary' }}
                          >
                            {available === null ? '…' : `${amount(available)} ${sku.unit}`}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2.5, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button disabled={busy} onClick={p.onClose}>
            取消
          </Button>
          <Button variant="contained" disabled={busy || !product || !!missing} onClick={submit}>
            {busy ? '正在提交…' : '确认入库'}
          </Button>
        </DialogActions>
      </Dialog>
      {editing && product && (
        <SkuCreateDialog
          defaultUsage={product.usage as any}
          categories={p.categories}
          sku={product}
          skus={p.skus}
          recipes={p.recipes}
          onClose={() => setEditing(false)}
          onCreated={() => {
            setEditing(false);
            p.onChanged();
          }}
        />
      )}
    </>
  );
}
